import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import * as VeridionExportActions from './veridion-export.actions';
import { VeridionExportService } from '../../services/veridion-export.service';
import { PopupService } from '../../services/popup.service';

@Injectable()
export class VeridionExportEffects {
  constructor(
    private actions$: Actions,
    private veridionExportService: VeridionExportService,
    private popupService: PopupService
  ) {}

  // ============================================
  // Validate Export Credits Effects
  // ============================================

  validateExportCredits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VeridionExportActions.validateExportCredits),
      switchMap(({ totalRecords, pageSize }) =>
        this.veridionExportService.validateExportCredits(totalRecords, pageSize).pipe(
          map((validation) =>
            VeridionExportActions.validateExportCreditsSuccess({ validation })
          ),
          catchError((error) =>
            of(VeridionExportActions.validateExportCreditsFailure({ error }))
          )
        )
      )
    )
  );

  // ============================================
  // Start Export Effects
  // ============================================

  startVeridionExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VeridionExportActions.startVeridionExport),
      switchMap(({ filters, totalRecords, pageSize }) =>
        this.veridionExportService.startExport(filters, totalRecords, pageSize).pipe(
          map((result) =>
            VeridionExportActions.startVeridionExportSuccess({ result })
          ),
          catchError((error) =>
            of(VeridionExportActions.startVeridionExportFailure({ error }))
          )
        )
      )
    )
  );

  startVeridionExportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VeridionExportActions.startVeridionExportSuccess),
        tap(({ result }) => {
          this.popupService.notify(
            result.message || 'Export queued successfully!',
            'Success',
            'success'
          );
        })
      ),
    { dispatch: false }
  );

  startVeridionExportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VeridionExportActions.startVeridionExportFailure),
        tap(({ error }) => {
          const message = error?.error?.message || error?.message || 'Failed to start export';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );

  // ============================================
  // Load Exports Effects
  // ============================================

  loadExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VeridionExportActions.loadExports),
      switchMap(({ status, page, perPage }) =>
        this.veridionExportService.listExports({
          status,
          page,
          per_page: perPage
        }).pipe(
          map((response) =>
            VeridionExportActions.loadExportsSuccess({
              exports: response.data,
              pagination: response.pagination
            })
          ),
          catchError((error) =>
            of(VeridionExportActions.loadExportsFailure({ error }))
          )
        )
      )
    )
  );

  loadExportsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VeridionExportActions.loadExportsFailure),
        tap(({ error }) => {
          const message = error?.error?.message || error?.message || 'Failed to load exports';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );

  // ============================================
  // Download Export Effects
  // ============================================

  downloadExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VeridionExportActions.downloadExport),
      switchMap(({ exportId, fileName }) =>
        this.veridionExportService.downloadExport(exportId).pipe(
          map(({ blob, fileName: responseFileName }) => {
            // Trigger file download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || responseFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return VeridionExportActions.downloadExportSuccess({ exportId });
          }),
          catchError((error) =>
            of(VeridionExportActions.downloadExportFailure({ exportId, error }))
          )
        )
      )
    )
  );

  downloadExportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VeridionExportActions.downloadExportSuccess),
        tap(() => {
          this.popupService.notify('Export downloaded successfully!', 'Success', 'success');
        })
      ),
    { dispatch: false }
  );

  downloadExportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VeridionExportActions.downloadExportFailure),
        tap(({ error }) => {
          const message = error?.error?.message || error?.message || 'Failed to download export';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );
}
